import { create } from 'zustand';
import type { Credits, LicenseSubscription, Plan } from '@/types/license';
import { getDocument } from '@/lib/firebase/firestore';
import { COLLECTIONS } from '@/config/constants';
import { idbCache } from '@/lib/cache/idb-cache';
import { useAppStore } from './app-store';
import { useAuthStore } from './auth-store';
import { ref, get as rtdbGet } from 'firebase/database';
import { rtdb } from '@/config/firebase';

// Same-origin on *.ulgebra.com (via hosting rewrite), fallback to cloudfunctions.net
const LIC_API_BASE =
  typeof window !== 'undefined' && window.location.hostname.endsWith('.ulgebra.com')
    ? ''
    : 'https://us-central1-ulgebra-license.cloudfunctions.net';
const IDB_KEY_LICENSE_SUBS = 'license_subscriptions';
const IDB_KEY_CREDITS = 'license_credits';
const IDB_TTL = 60 * 60 * 1000; // 1h

interface LicenseStore {
  credits: Credits | null;
  /** License subscriptions for the current user (from getUALicensedUserSubs) */
  licenseSubscriptions: LicenseSubscription[];
  plan: Plan | null;
  allocatedUsers: string[];
  loading: boolean;
  /**
   * True once license subscriptions have been resolved at least once
   * (either from IDB cache or a network fetch — success or error).
   * Trial gating UI must wait for this to flip true to avoid a paywall
   * flash on paid users while subs are still loading.
   */
  licenseLoaded: boolean;
  /**
   * True only after a network fetch (success or error) has completed.
   * The cancelled-vs-expired paywall copy depends on accurate subscription
   * status, so the paywall modal waits for this rather than `licenseLoaded`
   * (which can flip from stale cache).
   */
  licenseFreshFetched: boolean;
  error: string | null;

  fetchCredits(uid: string): Promise<void>;
  /** Fetch license subscriptions via getUALicensedUserSubs, filter by current appCode */
  fetchLicenseSubscriptions(): Promise<void>;
  /** Restore cached license data from IDB (async, call early in init) */
  restoreFromCache(): Promise<void>;
  setCredits(credits: Credits): void;
  setPlan(plan: Plan): void;
}

export const useLicenseStore = create<LicenseStore>((set) => ({
  credits: null,
  licenseSubscriptions: [],
  plan: null,
  allocatedUsers: [],
  loading: false,
  licenseLoaded: false,
  licenseFreshFetched: false,
  error: null,

  restoreFromCache: async () => {
    try {
      const [cachedSubs, cachedCredits] = await Promise.all([
        idbCache.get<LicenseSubscription[]>('org_data', IDB_KEY_LICENSE_SUBS),
        idbCache.get<Credits>('org_data', IDB_KEY_CREDITS),
      ]);
      const updates: Partial<{ licenseSubscriptions: LicenseSubscription[]; credits: Credits; licenseLoaded: boolean }> = {};
      // Presence of the cache entry (even an empty array) is a valid "loaded" signal.
      if (cachedSubs !== undefined && cachedSubs !== null) {
        updates.licenseSubscriptions = cachedSubs;
        updates.licenseLoaded = true;
      }
      if (cachedCredits) {
        cachedCredits.lastUpdated = new Date(cachedCredits.lastUpdated);
        updates.credits = cachedCredits;
      }
      if (Object.keys(updates).length > 0) {
        set(updates);
        console.log('[License] Restored from IDB cache — subs:', cachedSubs?.length ?? 0, 'credits:', !!cachedCredits);
      }
      // Credits are app-agnostic — fire regardless of whether cache existed.
      // The licensed-subs fresh fetch is owned by use-app-init (right after
      // parseAndSetAppCode), so we deliberately don't fire it here to avoid
      // double-billing the licensing Cloud Function on every page load.
      const uid = useAuthStore.getState().user?.uid;
      if (uid) useLicenseStore.getState().fetchCredits(uid).catch(() => {});
    } catch { /* IDB unavailable */ }
  },

  fetchCredits: async (uid) => {
    set({ loading: true });
    try {
      // Credits are stored in Realtime Database at ualic_user_credits/{uid} as a single number
      const creditsRef = ref(rtdb, `ualic_user_credits/${uid}`);
      const snapshot = await rtdbGet(creditsRef);
      const val = snapshot.val();
      const remaining = val ? Math.ceil(val * 10) / 10 : 0;

      const credits: Credits = {
        total: remaining, // RTDB only stores remaining balance
        used: 0,
        remaining,
        lastUpdated: new Date(),
      };
      set({ credits, loading: false });
      idbCache.set('org_data', IDB_KEY_CREDITS, credits, IDB_TTL).catch(() => {});
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch credits',
      });
    }
  },

  fetchLicenseSubscriptions: async () => {
    // Use the impersonated email when the dashboard admin is debugging
    // a customer ("act as that user"), otherwise the signed-in user's own
    // email. Seats live under each user's OWN email
    // (`UALicensedUsers/{email}/subscriptions`), so a shared user must be
    // looked up by their own address — NOT the admin's. Checking the admin's
    // email made every shared user of an admin who kept no seat for
    // themselves see "trial expired" despite holding an active seat.
    // Matches the legacy app (`fetchLICDetails(currentUser.email)`).
    const auth = useAuthStore.getState();
    const email = auth.impersonatedUserEmail || auth.user?.email;
    const extensionName = useAppStore.getState().extensionName;
    if (!email) {
      set({ licenseSubscriptions: [], loading: false, licenseLoaded: true, licenseFreshFetched: true });
      return;
    }

    set({ loading: true });
    try {
      const resp = await fetch(`${LIC_API_BASE}/getUALicensedUserSubs?userId=${encodeURIComponent(email)}`);
      const data = await resp.json() as {
        subscriptions?: Record<string, Record<string, unknown>>;
        inActiveSubscriptions?: Record<string, Record<string, unknown>>;
      };

      // Merge active + inactive subscriptions into a single list
      const allSubs: LicenseSubscription[] = [];

      const parseSubs = (map: Record<string, Record<string, unknown>> | undefined) => {
        if (!map) return;
        for (const [subId, sub] of Object.entries(map)) {
          allSubs.push({
            id: subId,
            subscriptionId: (sub.subscriptionId as string) || subId,
            priceId: (sub.priceId as string) || '',
            licOwner: (sub.licOwner as string) || '',
            appCode: (sub.appCode as string) || '',
            subscriptionStatus: (sub.subscriptionStatus as string) || '',
            modifiedTime: (sub.modifiedTime as number) || undefined,
          });
        }
      };

      parseSubs(data.subscriptions);
      parseSubs(data.inActiveSubscriptions);

      // Filter to current app if extensionName is available
      const filtered = extensionName
        ? allSubs.filter((s) => s.appCode.toLowerCase() === extensionName.toLowerCase())
        : allSubs;

      set({ licenseSubscriptions: filtered, loading: false, licenseLoaded: true, licenseFreshFetched: true });
      idbCache.set('org_data', IDB_KEY_LICENSE_SUBS, filtered, IDB_TTL).catch(() => {});
      console.log('[License] Fetched subscriptions:', filtered.map(s => `${s.id} → ${s.subscriptionStatus} (${s.appCode})`).join(', ') || 'none');
    } catch (error) {
      // Mark loaded even on error so the UI can proceed (paywall will fall back
      // to the cached value, or empty subs → expired-trial users get the paywall).
      set({
        loading: false,
        licenseLoaded: true,
        licenseFreshFetched: true,
        error: error instanceof Error ? error.message : 'Failed to fetch license subscriptions',
      });
    }
  },

  setCredits: (credits) => set({ credits }),
  setPlan: (plan) => set({ plan }),
}));
